/**
 * Página Mis guías - Pantalla dedicada a las guías del usuario
 * Mismo diseño que el Dashboard (Topbar + Sidebar + contenido)
 * Ruta: /mis-guias
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, Typography, Button, LinearProgress, Chip } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout, MyGuidesSection } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../components/PurchaseChoiceModal";
import { extractCoverFromPages } from "../utils/guideCover";
import { getApiOrigin } from "../utils/api";
import { trackGuideInteraction } from "../utils/analytics";

import "../components/Dashboard/Dashboard.css";

const MyGuidesPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    user,
    subscription,
    limits,
    handleLogout: contextLogout,
    readProjects,
    readPages,
    deleteProject,
    createProject,
    createPage,
    createSharedGuide,
    getSharedGuides,
    canCreateGuide,
    canExport,
    purchaseSubscription,
    refreshSubscription,
  } = useFirebase();

  const [showLogin, setShowLogin] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [purchaseChoiceModal, setPurchaseChoiceModal] = useState({
    open: false,
    guide: null,
    limitInfo: null,
  });
  const [myGuides, setMyGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [guideCoverImages, setGuideCoverImages] = useState({});

  useEffect(() => {
    const loadGuides = async () => {
      if (!user) {
        setMyGuides([]);
        setGuideCoverImages({});
        setLoadingGuides(false);
        return;
      }
      setLoadingGuides(true);
      try {
        const userProjects = await readProjects();
        setMyGuides(userProjects || []);
      } catch (error) {
        console.error("Error loading guides:", error);
        setMyGuides([]);
      } finally {
        setLoadingGuides(false);
      }
    };
    loadGuides();
  }, [user, readProjects]);

  // Fallback: si el proyecto no tiene coverImage (screenshot), extraer de las páginas
  useEffect(() => {
    if (!user || !myGuides.length || !readPages) return;
    const apiOrigin = getApiOrigin();
    myGuides.forEach((guide) => {
      if (guide.coverImage || guideCoverImages[guide.id]) return;
      readPages(guide.id)
        .then((pages) => {
          if (!pages || pages.length === 0) return;
          const url = extractCoverFromPages(pages, apiOrigin);
          if (url) setGuideCoverImages((prev) => ({ ...prev, [guide.id]: url }));
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, myGuides, readPages]);

  const formattedUser = user
    ? {
        id: user.uid || user.id,
        displayName: user.displayName || user.name || user.email?.split("@")[0] || "Usuario",
        email: user.email,
      }
    : null;

  const handleSectionChange = (sectionId) => {
    if (sectionId === "home") {
      navigate("/");
      return;
    }
    if (sectionId === "explore") {
      navigate("/explorar");
      return;
    }
    if (sectionId === "my-guides") return;
    if (sectionId === "create") {
      navigate("/crear-guia");
      return;
    }
    if (sectionId === "favorites") {
      if (!user) {
        setAuthMode("login");
        setShowLogin(true);
        enqueueSnackbar("Inicia sesión para ver tus favoritos", { variant: "info" });
        return;
      }
      navigate("/favoritos");
      return;
    }
    if (sectionId === "recent") {
      navigate("/");
      return;
    }
    if (sectionId === "passport") {
      navigate("/passport");
      return;
    }
    if (sectionId === "travel-card") {
      navigate("/tarjeta-viajera");
      return;
    }
    // logros-viajeros comentado
    // if (sectionId === "logros-viajeros") {
    //   if (!user) {
    //     setAuthMode("login");
    //     setShowLogin(true);
    //     enqueueSnackbar("Inicia sesión para ver tus logros viajeros", { variant: "info" });
    //     return;
    //   }
    //   navigate("/logros-viajeros");
    //   return;
    // }
    if (sectionId === "modules") {
      if (!user) {
        setAuthMode("login");
        setShowLogin(true);
        enqueueSnackbar("Inicia sesión para ver tus módulos", { variant: "info" });
        return;
      }
      navigate("/modulos");
      return;
    }
    if (sectionId === "discounts") {
      navigate("/descuentos");
      return;
    }
    if (sectionId === "help") {
      navigate("/", { state: { section: "help" } });
      return;
    }
    if (sectionId === "settings") {
      if (!user) {
        setAuthMode("login");
        setShowLogin(true);
        enqueueSnackbar("Inicia sesión para acceder a la configuración", { variant: "info" });
        return;
      }
      navigate("/settings");
      return;
    }
  };

  const handleCreateFromScratch = async () => {
    if (user) {
      const createCheck = canCreateGuide();
      if (!createCheck.allowed) {
        setPurchaseChoiceModal({
          open: true,
          guide: { id: "new", name: "Nueva guía" },
          limitInfo: {
            currentGuides: createCheck.currentGuides,
            maxGuides: createCheck.maxGuides,
          },
        });
        return;
      }
    }
    navigate("/crear-guia");
  };

  const handleOpenMyGuide = async (guideId) => {
    if (!user) {
      setAuthMode("login");
      setShowLogin(true);
      return;
    }
    trackGuideInteraction("opened", guideId, { source: "mis-guias" });
    try {
      const project = myGuides.find((p) => p.id === guideId);
      if (project?.userId) {
        navigate(`/usuarios/${project.userId}/guias/${guideId}`);
      } else {
        const userId = user.uid || user.id;
        navigate(`/usuarios/${userId}/guias/${guideId}`);
      }
    } catch (error) {
      console.error("Error opening guide:", error);
      enqueueSnackbar("Error al abrir la guía", { variant: "error" });
    }
  };

  const handleExportMyGuide = (guideId) => {
    if (!canExport()) {
      setPurchaseChoiceModal({
        open: true,
        guide: { id: guideId, name: myGuides.find((g) => g.id === guideId)?.name || "Tu guía" },
        limitInfo: {
          currentGuides: canCreateGuide().currentGuides ?? 0,
          maxGuides: canCreateGuide().maxGuides ?? 1,
        },
      });
      return;
    }
    enqueueSnackbar("Función de exportación próximamente", { variant: "info" });
  };

  const handleDeleteGuide = async (guideId) => {
    trackGuideInteraction("deleted", guideId, { source: "mis-guias" });
    try {
      await deleteProject(guideId);
      // Recargar lista de guías y límites para que el sistema tenga el conteo actualizado
      const userProjects = await readProjects();
      setMyGuides(userProjects || []);
      await refreshSubscription();
      enqueueSnackbar("Guía eliminada", { variant: "success" });
    } catch (error) {
      console.error("Error deleting guide:", error);
      enqueueSnackbar("Error al eliminar la guía", { variant: "error" });
    }
  };

  const handleShareGuide = async (guideId) => {
    trackGuideInteraction("share_clicked", guideId, { source: "mis-guias" });
    try {
      const list = await getSharedGuides();
      const publicLink = Array.isArray(list)
        ? list.find((s) => s.projectId === guideId && s.isPublic === true)
        : null;
      const url = publicLink?.url;
      if (url) {
        navigator.clipboard.writeText(url);
        enqueueSnackbar("Enlace copiado al portapapeles", { variant: "success" });
        return;
      }
      const result = await createSharedGuide(guideId, true);
      if (result?.error === "TEMPLATE_CANNOT_BE_SHARED_PUBLICLY" || result?.error === "OFFICIAL_TEMPLATE_CANNOT_BE_SHARED_PUBLICLY") {
        enqueueSnackbar("Las guías basadas en plantillas no se pueden compartir públicamente.", { variant: "warning" });
        return;
      }
      const newUrl = result?.shareUrl ?? result?.url;
      if (newUrl) {
        navigator.clipboard.writeText(newUrl);
        enqueueSnackbar("Enlace copiado al portapapeles", { variant: "success" });
      } else {
        enqueueSnackbar("Guía compartida", { variant: "success" });
      }
    } catch (error) {
      console.error("Error sharing guide:", error);
      enqueueSnackbar("Error al compartir la guía", { variant: "error" });
    }
  };

  const handleViewMyGuide = async (guideId) => {
    if (!user) {
      setAuthMode("login");
      setShowLogin(true);
      return;
    }
    trackGuideInteraction("preview_clicked", guideId, { source: "mis-guias" });
    try {
      const list = await getSharedGuides();
      const privateLink = Array.isArray(list)
        ? list.find((s) => s.projectId === guideId && s.isPublic === false)
        : null;
      const viewUrl = privateLink?.url;
      if (viewUrl) {
        const path = viewUrl.startsWith("/") ? viewUrl : new URL(viewUrl).pathname;
        navigate(path);
        return;
      }
      const result = await createSharedGuide(guideId, false);
      const newUrl = result?.shareUrl ?? result?.url;
      if (newUrl) {
        const path = newUrl.startsWith("/") ? newUrl : new URL(newUrl).pathname;
        navigate(path);
      } else if (result?.token ?? result?.shareToken) {
        navigate(`/view/${result.token ?? result.shareToken}`);
      } else {
        enqueueSnackbar("No se pudo abrir la vista previa", { variant: "warning" });
      }
    } catch (error) {
      console.error("Error opening preview:", error);
      enqueueSnackbar("Error al abrir la vista previa", { variant: "error" });
    }
  };

  const handleLogout = async () => {
    await contextLogout();
    setMyGuides([]);
    enqueueSnackbar("Sesión cerrada", { variant: "success" });
  };

  const filteredGuides = searchQuery
    ? myGuides.filter((g) =>
        g.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : myGuides;

  return (
    <>
      <DashboardLayout
        user={formattedUser}
        activeSection="my-guides"
        onSectionChange={handleSectionChange}
        onLogin={() => { setAuthMode("login"); setShowLogin(true); }}
        onSignUp={() => { setAuthMode("signup"); setShowLogin(true); }}
        onLogout={handleLogout}
        onCreateNew={handleCreateFromScratch}
        onSearch={(q) => setSearchQuery(q || "")}
        myGuides={myGuides}
      >
        {!user ? (
          /* Sin sesión: mensaje para iniciar sesión */
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              backgroundColor: "#fff",
              borderRadius: "24px",
              border: "2px dashed rgba(99, 102, 241, 0.2)",
              maxWidth: 480,
              mx: "auto",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "rgba(99, 102, 241, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 40, color: "#6366F1" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#1a1a2e", fontWeight: 600, mb: 1 }}>
              Mis guías
            </Typography>
            <Typography sx={{ color: "#64748B", fontSize: "0.938rem", mb: 3 }}>
              Inicia sesión para ver y gestionar tus guías de viaje
            </Typography>
            <Button
              variant="contained"
              onClick={() => { setAuthMode("login"); setShowLogin(true); }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.938rem",
                background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                borderRadius: "12px",
                px: 3,
                py: 1.25,
                boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
                  boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
                },
              }}
            >
              Iniciar sesión
            </Button>
          </Box>
        ) : (
          <>
            {/* Banner de límites del usuario */}
            {limits && !subscription?.isComplete && (
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)",
                  borderRadius: "16px",
                  border: "1px solid rgba(99, 102, 241, 0.15)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1a1a2e" }}>
                        Plan Gratuito
                      </Typography>
                      <Chip 
                        label={`${myGuides.length}/${limits.maxGuides} guías`}
                        size="small"
                        sx={{
                          backgroundColor: myGuides.length >= limits.maxGuides ? "rgba(239, 68, 68, 0.1)" : "rgba(99, 102, 241, 0.1)",
                          color: myGuides.length >= limits.maxGuides ? "#DC2626" : "#6366F1",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((myGuides.length / limits.maxGuides) * 100, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "rgba(99, 102, 241, 0.15)",
                        "& .MuiLinearProgress-bar": {
                          background: myGuides.length >= limits.maxGuides 
                            ? "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)"
                            : "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)",
                          borderRadius: 3,
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#64748B", mt: 0.5, display: "block" }}>
                      {limits.maxPagesPerGuide} páginas por guía • Sin exportación
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setPurchaseChoiceModal({
                      open: true,
                      guide: { id: "upgrade", name: "Plan Pro" },
                      limitInfo: {
                        currentGuides: limits?.currentGuides ?? 0,
                        maxGuides: limits?.maxGuides ?? 1,
                      },
                    })}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                      borderRadius: "12px",
                      px: 2.5,
                      py: 1,
                      boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
                      },
                    }}
                  >
                    Actualizar a Pro
                  </Button>
                </Box>
              </Box>
            )}

            {/* Banner para suscriptores */}
            {subscription?.isComplete && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)",
                  borderRadius: "16px",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <LockOpenIcon sx={{ color: "#10B981", fontSize: 24 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#065F46" }}>
                    Suscripción Pro activa
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#047857" }}>
                    Guías ilimitadas • Páginas ilimitadas • Exportación disponible
                  </Typography>
                </Box>
              </Box>
            )}

            <MyGuidesSection
              guides={filteredGuides.map((g) => ({
                ...g,
                coverImage: g.coverImage || guideCoverImages[g.id],
              }))}
              loading={loadingGuides}
              onOpenGuide={handleOpenMyGuide}
              onViewGuide={handleViewMyGuide}
              onDeleteGuide={handleDeleteGuide}
              onCreateNew={handleCreateFromScratch}
              onSeeAll={() => navigate("/")}
            />
          </>
        )}
      </DashboardLayout>

      <BasicModal
        width={500}
        showModal={showLogin}
        handleClose={() => setShowLogin(false)}
      >
        <Auth setShowLogin={setShowLogin} initialMode={authMode} />
      </BasicModal>

      <PurchaseChoiceModal
        open={purchaseChoiceModal.open}
        guide={purchaseChoiceModal.guide}
        onClose={() =>
          setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null })
        }
        onBuyGuide={() =>
          setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null })
        }
        onSubscribe={async () => {
          try {
            await purchaseSubscription();
          } catch (error) {
            enqueueSnackbar(error.message || "Error al iniciar la suscripción", {
              variant: "error",
            });
            setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
            throw error;
          }
        }}
        limitInfo={purchaseChoiceModal.limitInfo}
      />
    </>
  );
};

export default MyGuidesPage;
